import { IconButton, Box as MUIBox, styled, Typography } from '@mui/material'
import { rgba } from 'polished'

import { statusChipBase } from 'components/Status/Status.styles'

import { theme } from 'styles/theme'

export const CloseButton = styled(IconButton)(() => ({
  cursor: 'pointer',
  position: 'absolute',
  right: 5,
  top: 5
}))

export const Header = styled('div')(({ theme }) => ({
  alignItems: 'start',
  borderBottom: `3px solid ${theme.palette.neutral[700]}`,
  display: 'grid',
  gap: 10,
  gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
  paddingBottom: 10
}))

export const Heading = styled(Typography)`
  align-items: center;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`

export const StatusText = styled(Typography)(() => ({
  fontSize: '14px'
}))

export const Box = styled(MUIBox)(() => ({
  marginTop: '-50px',
  display: 'flex',
  flexDirection: 'column'
}))

export const TableWrapper = styled('div')(() => ({
  flex: 1,
  height: '100%',
  overflowY: 'auto'
}))

interface NotifyProps {
  notViewed: boolean
}

export const Separator = styled('span')(() => ({
  width: '8px'
}))

export const NotifyWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '16px'
}))

export const Notify = styled('div')<NotifyProps>(({ notViewed, theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: notViewed ? theme.palette.success[300] : theme.palette.error[300]
}))

// Status Chip Styles
const getStatusStyles = (status: string) => {
  switch (status) {
    case 'pending':
      return {
        color: theme.palette.neutral[400],
        background: rgba(theme.palette.neutral[700], 0.3)
      }
    case 'processing':
    case 'generating':
      return {
        color: theme.palette.secondary[200],
        background: rgba(theme.palette.secondary[200], 0.1)
      }
    case 'generated':
      return {
        color: theme.palette.primary[200],
        background: rgba(theme.palette.primary[200], 0.2)
      }
    case 'error':
      return {
        color: theme.palette.error[300],
        background: rgba(theme.palette.error[300], 0.1)
      }
    default:
      return {
        color: theme.palette.neutral[400],
        background: rgba(theme.palette.neutral[700], 0.3)
      }
  }
}

type StatusChipProps = {
  status: string
}

export const StatusChip = styled('div')<StatusChipProps>(({ status }) => ({
  ...statusChipBase,
  ...getStatusStyles(status)
}))

// New Report Section Styles
export const PageWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

export const SectionCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[800],
  borderRadius: '12px',
  padding: '24px',
  border: `1px solid ${theme.palette.neutral[700]}`
}))

export const SectionTitle = styled(Typography)(() => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
}))

export const SectionDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.neutral[400],
  marginBottom: '24px'
}))

export const FormRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  alignItems: 'flex-start',
  marginBottom: '16px'
}))

export const FormField = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: '200px'
}))

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.neutral[200]
}))

export const RadioGroup = styled('div')(() => ({
  display: 'flex',
  gap: '24px',
  alignItems: 'center'
}))

export const RadioOption = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: theme.palette.neutral[200]
}))

export const InfoBox = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '16px',
  backgroundColor: rgba(theme.palette.secondary[200], 0.08),
  borderRadius: '8px',
  marginBottom: '24px',
  border: `1px solid ${rgba(theme.palette.secondary[200], 0.2)}`
}))

export const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.neutral[300],
  lineHeight: 1.5
}))

export const ButtonWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '12px',
  marginTop: '8px'
}))

export const DatePickerWrapper = styled('div')(({ theme }) => ({
  '& .reportrange': {
    border: `1px solid ${theme.palette.neutral[600]}`,
    borderRadius: '8px',
    padding: '10px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '240px',
    '&:hover': {
      borderColor: theme.palette.neutral[500]
    }
  }
}))

export const ReportTypeCard = styled('div')<{ isSelected?: boolean }>(
  ({ theme, isSelected }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    borderRadius: '8px',
    border: `2px solid ${
      isSelected ? theme.palette.primary.main : theme.palette.neutral[700]
    }`,
    backgroundColor: isSelected
      ? rgba(theme.palette.primary.main, 0.05)
      : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: isSelected
        ? theme.palette.primary.main
        : theme.palette.neutral[500]
    }
  })
)

export const ReportTypeTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.neutral[100]
}))

export const ReportTypeDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.neutral[400]
}))

export const Divider = styled('div')(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.palette.neutral[700],
  margin: '32px 0'
}))

export const TabsWrapper = styled('div')(() => ({
  marginBottom: '24px'
}))

export const ContentWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[900],
  borderRadius: '0 12px 12px 12px',
  padding: '24px',
  minHeight: '400px'
}))

export const DatePickerButton = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[600]}`,
  borderRadius: '8px',
  padding: '10px 16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: '240px',
  '&:hover': {
    borderColor: theme.palette.neutral[500]
  }
}))

export const FilterModalContent = styled('div')(() => ({
  display: 'flex',
  gap: '24px',
  flexWrap: 'wrap'
}))

export const FilterColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
}))

export const FilterOptionsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
}))

export const FilterOption = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '4px',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: theme.palette.neutral[200]
}))

export const PresetPeriodItem = styled('div')(({ theme }) => ({
  cursor: 'pointer',
  padding: '8px 12px',
  borderRadius: '8px',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.neutral[800]
  }
}))
